import BackButton from '@/components/ui/BackButton'
import Skeleton from '@/components/ui/Skeleton'
import { cn } from '@/lib/utils'
import {
  BAR_CELL,
  BAR_ROW,
  LIST_CELL,
  LIST_HEADER,
  LIST_ROW,
  NAV_GRID,
  NAV_LINK,
  NOTES_CARD,
  NOTES_CELL,
  PLAYER_GRID,
  STAGE_CELL,
} from '@/screens/courses/components/player/player-box'

/**
 * The lecture page before its course has loaded, block for block, built on
 * the loaded page's own boxes (`player-box.ts`). Heights, from the classes
 * both share (a text line is a bar centred in the loaded LINE's height),
 * snapped to the 4-grid (the 2026-09-28 design refresh):
 *
 *   header      eyebrow 16 · 4 · back button / h1 line 36        = 56
 *   player      16:9 of the column
 *   bar         row min 44 (36 from lg) — meta line 20 in it
 *               16 · two nav cards 12 + 16 + 0 + 16 + 12 + 2 hairlines = 58
 *   list card   header 12 + heading 44 narrow (20 from @4xl) + 4 + 16 + 12,
 *               then (from @4xl only; collapsed narrower) a section heading
 *               16 + 16 + 4 and eight 56px rows. The loaded list is as long
 *               as the course — this is its first screenful. A course the
 *               member owns adds its progress bar (4 + 6) under the header.
 *   notes card  20 + heading 24 + 8 + three body lines of 20 + 20 = 132,
 *               for a lecture with a description; none draw no card.
 *
 * Light on purpose: the route's pending and error components draw it, and
 * they ship in the entry chunk — `Skeleton`, `BackButton` and class strings
 * only.
 */

function Line({ height, className }: { height: string; className: string }) {
  return (
    <div className={cn('flex items-center', height)}>
      <Skeleton className={className} />
    </div>
  )
}

/**
 * The TitleRow's shape: the course title as eyebrow, the back button, the
 * lecture title. The button is the real one — it needs no data: a Link given
 * no params keeps the route's own, so it goes to this page's course.
 */
export function LectureHeaderSkeleton() {
  return (
    <div className="flex flex-col gap-1">
      {/* Figma 15 → h-4 (4-grid). */}
      <Line height="h-4" className="h-2 w-48 rounded-xs" />
      <div className="flex items-start gap-2">
        <div className="flex h-9 shrink-0 items-center">
          <BackButton to="/courses/$courseId" />
        </div>
        {/* Figma 34 → h-9 (4-grid). */}
        <Line height="h-9 min-w-0 flex-1" className="h-6 w-72 max-w-full rounded-sm" />
      </div>
    </div>
  )
}

const ROWS = ['w-4/5', 'w-3/5', 'w-11/12', 'w-2/3', 'w-3/4', 'w-1/2', 'w-4/5', 'w-2/3'] as const

export function LectureBodySkeleton() {
  return (
    <div aria-hidden="true" className={PLAYER_GRID}>
      <div className={STAGE_CELL}>
        <Skeleton className="aspect-video w-full rounded-lg" />
      </div>

      <div className={BAR_CELL}>
        <div className={BAR_ROW}>
          {/* Figma 21 → h-5 (4-grid). */}
          <Line height="h-5" className="h-4 w-56 rounded-xs" />
        </div>
        <div className={NAV_GRID}>
          {(['previous', 'next'] as const).map((side) => (
            <div
              key={side}
              className={cn(NAV_LINK, side === 'next' ? 'col-start-2 items-end' : null)}
            >
              {/* Figma 15 → h-4 (4-grid). */}
              <Line height="h-4" className="h-2 w-16 rounded-xs" />
              {/* Figma 18 → h-4 (4-grid tie, non-interactive). */}
              <Line height="h-4" className="h-4 w-4/5 rounded-xs" />
            </div>
          ))}
        </div>
      </div>

      <div className={LIST_CELL}>
        <div className={LIST_HEADER}>
          <Line height="h-11 @4xl:h-5" className="h-4 w-20 rounded-xs" />
          {/* Figma 17 → h-4 (4-grid). */}
          <Line height="h-4" className="h-3 w-36 rounded-xs" />
        </div>
        <div className="hidden flex-col overflow-hidden @4xl:flex">
          <div className="px-4 pt-4 pb-1">
            {/* Figma 15 → h-4 (4-grid). */}
            <Line height="h-4" className="h-2 w-32 rounded-xs" />
          </div>
          {ROWS.map((width, index) => (
            <div key={index} className={LIST_ROW}>
              {/* Figma 18 → h-4 (4-grid tie, non-interactive). */}
              <Line height="h-4 w-6 shrink-0 justify-center" className="h-3 w-3 rounded-xs" />
              {/* Figma 2 → 0 (4-grid tie, non-interactive: nearest 8 of a 2px gap is 0). */}
              <div className="flex min-w-0 flex-1 flex-col gap-0">
                <Line height="h-4" className={cn('h-4 rounded-xs', width)} />
                {/* Figma 17 → h-4 (4-grid). */}
                <Line height="h-4" className="h-3 w-10 rounded-xs" />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={cn(NOTES_CELL, NOTES_CARD)}>
        <div className="flex flex-col gap-2">
          {/* Figma 22 → h-6 (4-grid tie, non-interactive). */}
          <Line height="h-6" className="h-4 w-40 rounded-xs" />
          <div className="flex flex-col">
            {/* Figma 21 → h-5 (4-grid). */}
            <Line height="h-5" className="h-4 w-full rounded-xs" />
            <Line height="h-5" className="h-4 w-11/12 rounded-xs" />
            <Line height="h-5" className="h-4 w-2/3 rounded-xs" />
          </div>
        </div>
      </div>
    </div>
  )
}
